import { FormError, FormField, FormHelper, FormInputGroup, FormLabel, Link, TextInput } from '@vp/swan'
import { useState } from 'react'
import type { Control } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useAppData } from '@app/providers'
import { OptionalIndicator } from '@shared/ui/form-label-indicators'
import type { IssueDescriptionFields } from '../../../config/issue-description-fields'
import { useIdentity } from '@vp/ubik-context'
import { useOrders } from '../../../hooks/use-orders'

const ORDER_NUMBER_PATTERN = /^vp_[a-z\d]+$/i

interface Props {
  control: Control<IssueDescriptionFields>
}

export const OrderNumberField = ({ control }: Props) => {
  const { t } = useTranslation()
  const { isSignedIn } = useIdentity()
  const [formatError, setFormatError] = useState<string | null>(null)
  const { ordersData, showOrderNumberInput } = useAppData()
  const { hasFailed: ordersFailed } = useOrders()
  const validateFormat = (value: string) => {
    const trimmed = value.trim()

    if (!trimmed || !!ORDER_NUMBER_PATTERN.test(trimmed)) {
      setFormatError(null)
      return
    }

    setFormatError('contact_us.page.steps.issue_description.order_error_invalid')
  }

  const isEmpty = Array.isArray(ordersData) && ordersData?.length === 0 // orders loaded but empty
  const shouldShow = showOrderNumberInput || ordersFailed || isEmpty // show this field if user has no orders, if loading orders failed, or if the user has explicitly chosen to enter order number manually

  if (!shouldShow) return null

  return (
    <FormField data-testid="manual-order-number-field">
      <FormLabel>
        {t('contact_us.page.steps.issue_description.manual_order_label')}
        <OptionalIndicator />
      </FormLabel>
      <Controller
        name="orderNumber"
        control={control}
        rules={{
          required: false,
          validate: (v) => {
            const trimmed = v.trim()
            if (!trimmed) return true
            return ORDER_NUMBER_PATTERN.test(trimmed) || 'contact_us.page.steps.issue_description.order_error_invalid'
          },
        }}
        render={({ field }) => (
          <FormInputGroup>
            <TextInput
              data-testid="manual-order-number-input"
              placeholder={t('contact_us.page.steps.issue_description.order_placeholder')}
              {...field}
              onBlur={(e) => {
                field.onBlur()
                validateFormat(e.target.value)
              }}
              onChange={(e) => {
                field.onChange(e)
                if (formatError) validateFormat(e.target.value)
              }}
            />
            {!formatError && (
              <FormHelper>
                {!isSignedIn ? (
                  t('contact_us.page.steps.issue_description.order_helper_guest_prefix')
                ) : (
                  <Trans
                    i18nKey="contact_us.page.steps.issue_description.order_helper_prefix"
                    components={{ orderHistoryLink: <Link target="_blank" to="/oh" /> }}
                  />
                )}
              </FormHelper>
            )}
            {formatError && <FormError data-testid="order-number-error">{t(formatError)}</FormError>}
          </FormInputGroup>
        )}
      />
    </FormField>
  )
}
